import _ from 'lodash'
import DomainIcon from 'mdi-react/DomainIcon'
import React from 'react'

import { Avatar, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'

import AsyncSelect, { AsyncSelectProps } from '../Async/Async.component'
import { autoCompleteSearch } from './AutoComplete.api'
import { AutoCompleteDataType, AutoCompleteOption } from './AutoComplete.model'
import styles from './AutoComplete.styles'

class AutoComplete extends React.PureComponent<AutoCompleteProps & WithStyles<typeof styles>> {
  constructor(props: AutoCompleteProps & WithStyles<typeof styles>) {
    super(props)

    this.autoCompleteHandler = _.debounce(this.autoCompleteHandler.bind(this), 250)
    this.getOptions = this.getOptions.bind(this)
    this.getLabel = this.getLabel.bind(this)
  }

  // React select only supports debounce if callbacks are used
  autoCompleteHandler(inputValue: string, callback: (options: any) => void) {
    this.getOptions(inputValue).then(callback)
  }

  async getOptions(inputValue: string) {
    const { dataType, dataLength } = this.props
    if (inputValue === '') {
      return []
    }
    const response = await autoCompleteSearch(inputValue, dataType, dataLength)
    return response.data
  }

  getNoOptionsMessage({ inputValue }: { inputValue: string }) {
    if (!inputValue) {
      return 'Please type to search...'
    } else {
      return `The search ${inputValue} didn't throw any results`
    }
  }

  getLabel(option: any, { context }: any) {
    const { classes } = this.props
    if (option.__isNew__ && context === 'menu') {
      return option.label
    }
    const labelData = option.display
    if (!labelData) {
      return 'Unknown data'
    }
    if (context === 'value') {
      return `${labelData.label}${labelData.extraInfo ? ` (${labelData.extraInfo})` : ''}`
    } else {
      return (
        <ListItem disableGutters dense={!!labelData.extraInfo}>
          {labelData.image !== undefined && (
            <ListItemIcon>
              <Avatar alt={labelData.label} src={labelData.image} className={classes.avatar}>
                <DomainIcon color="white" />
              </Avatar>
            </ListItemIcon>
          )}
          <ListItemText primary={labelData.label} secondary={labelData.extraInfo} />
        </ListItem>
      )
    }
  }

  render() {
    const { dataType, classes, isValidNewOption = (inputValue: string) => !!inputValue, ...selectProps } = this.props
    return (
      <AsyncSelect
        loadOptions={this.autoCompleteHandler}
        // This value is only use for key purposes
        getOptionValue={(option: AutoCompleteOption) =>
          option.display && `${option.display.label}-${option.display.extraInfo}`
        }
        formatOptionLabel={this.getLabel}
        noOptionsMessage={this.getNoOptionsMessage}
        isValidNewOption={isValidNewOption}
        {...selectProps}
      />
    )
  }
}

export type AutoCompleteProps = AsyncSelectProps & {
  dataType: AutoCompleteDataType
  dataLength?: number
}

export default withStyles(styles)(AutoComplete)
